import type { RoleCaps, RoleKey, Snapshot } from '@piaip/shared';

export interface Me {
  id: string;
  name: string;
  role: RoleKey;
  roleLabel: string;
  mustChangePassword: boolean;
  can: RoleCaps;
}

export interface AdminUser {
  id: string;
  name: string;
  role: RoleKey;
  roleLabel: string;
  roleDesc: string;
  mustChangePassword: boolean;
  createdAt: string;
}

export interface AuditEntry {
  at: string;
  user_id: string;
  user_name: string;
  action: string;
  detail: string;
}

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    ...init,
    headers: {
      ...(init.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      ...(init.headers ?? {}),
    },
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let body: any = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
  }
  if (!res.ok) {
    throw new ApiError(
      res.status,
      body?.error ?? (res.status === 401 ? 'Your session has ended. Sign in again.' : 'That did not work.'),
    );
  }
  return body as T;
}

const json = (body: unknown) => ({ body: JSON.stringify(body) });

export const api = {
  config: () => request<{ orgName: string; schemeTitle: string }>('/config'),

  me: () => request<{ user: Me | null }>('/auth/me'),
  login: (id: string, password: string) =>
    request<{ user: Me }>('/auth/login', { method: 'POST', ...json({ id, password }) }),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),
  renameSelf: (name: string) => request<{ user: Me }>('/me', { method: 'PATCH', ...json({ name }) }),
  changePassword: (current: string, next: string) =>
    request<{ ok: true; user: Me }>('/me/password', { method: 'POST', ...json({ current, next }) }),

  snapshot: () => request<{ snapshot: Snapshot }>('/snapshot'),

  createWeek: () => request<{ id: string; no: number }>('/weeks', { method: 'POST' }),
  patchWeek: (id: string, patch: Record<string, unknown>) =>
    request<{ ok: true }>(`/weeks/${id}`, { method: 'PATCH', ...json(patch) }),
  deleteWeek: (id: string) => request<{ ok: true }>(`/weeks/${id}`, { method: 'DELETE' }),
  submitWeek: (id: string) => request<{ ok: true }>(`/weeks/${id}/submit`, { method: 'POST' }),
  approveWeek: (id: string) => request<{ ok: true }>(`/weeks/${id}/approve`, { method: 'POST' }),
  returnWeek: (id: string, note: string) =>
    request<{ ok: true }>(`/weeks/${id}/return`, { method: 'POST', ...json({ note }) }),

  uploadPhotos: (weekId: string, files: File[]) => {
    const form = new FormData();
    files.forEach((f) => form.append('photos', f));
    return request<{ ids: string[] }>(`/weeks/${weekId}/photos`, { method: 'POST', body: form });
  },
  captionPhoto: (id: string, cap: string) =>
    request<{ ok: true }>(`/photos/${id}`, { method: 'PATCH', ...json({ cap }) }),
  tagPhoto: (id: string, comp: string) =>
    request<{ ok: true }>(`/photos/${id}`, { method: 'PATCH', ...json({ comp }) }),
  deletePhoto: (id: string) => request<{ ok: true }>(`/photos/${id}`, { method: 'DELETE' }),

  createIssue: () => request<{ id: string }>('/issues', { method: 'POST', ...json({}) }),
  patchIssue: (id: string, patch: Record<string, unknown>) =>
    request<{ ok: true }>(`/issues/${id}`, { method: 'PATCH', ...json(patch) }),
  deleteIssue: (id: string) => request<{ ok: true }>(`/issues/${id}`, { method: 'DELETE' }),

  createPackage: () => request<{ id: string }>('/packages', { method: 'POST', ...json({}) }),
  patchPackage: (id: string, patch: Record<string, unknown>) =>
    request<{ ok: true }>(`/packages/${id}`, { method: 'PATCH', ...json(patch) }),
  deletePackage: (id: string) => request<{ ok: true }>(`/packages/${id}`, { method: 'DELETE' }),
  assign: (componentKey: string, packageId: string) =>
    request<{ ok: true }>(`/assignments/${componentKey}`, { method: 'PUT', ...json({ packageId }) }),

  createPayment: (body: Record<string, unknown>) =>
    request<{ id: string }>('/payments', { method: 'POST', ...json(body) }),
  patchPayment: (id: string, patch: Record<string, unknown>) =>
    request<{ ok: true }>(`/payments/${id}`, { method: 'PATCH', ...json(patch) }),
  deletePayment: (id: string) => request<{ ok: true }>(`/payments/${id}`, { method: 'DELETE' }),

  setMisc: (key: string, value: number | string) =>
    request<{ ok: true; value: number }>(`/misc/${key}`, { method: 'PUT', ...json({ value }) }),

  users: () => request<{ users: AdminUser[] }>('/users'),
  createUser: (body: { id: string; name: string; password: string; role: RoleKey }) =>
    request<{ user: Me }>('/users', { method: 'POST', ...json(body) }),
  patchUser: (id: string, patch: { name?: string; role?: RoleKey; password?: string }) =>
    request<{ user: Me }>(`/users/${id}`, { method: 'PATCH', ...json(patch) }),
  deleteUser: (id: string) => request<{ ok: true }>(`/users/${id}`, { method: 'DELETE' }),
  audit: () => request<{ entries: AuditEntry[] }>('/audit'),

  clearData: () => request<{ ok: true }>('/data/clear', { method: 'POST' }),
  importBackup: (file: File, restoreUsers: boolean) => {
    const form = new FormData();
    form.append('backup', file);
    form.append('restoreUsers', String(restoreUsers));
    return request<{ ok: true }>('/backup', { method: 'POST', body: form });
  },
  backupUrl: '/api/backup',
};
